/**
 * Hook: usePipelinesList
 *
 * Lista pipelines disponíveis para o usuário no tenant atual
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth';
import { ROLES } from '@/features/auth/types';
import { Pipeline, PipelinePerfilUsuario, PipelineType, PIPELINE_TYPE_ORDER } from '../types';

export function usePipelinesList(tipo?: PipelineType, options?: { all?: boolean }) {
  const { profile } = useAuth();
  const listAll = options?.all ?? false;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['pipelinesList', profile?.tenant_id, profile?.role, tipo, listAll],
    queryFn: async () => {
      if (!profile?.tenant_id) {
        throw new Error('Tenant ID não encontrado');
      }

      return await fetchPipelinesList(profile.tenant_id, profile.role, tipo, listAll);
    },
    enabled: !!profile?.tenant_id,
    staleTime: 1000 * 60 * 5,
    retry: 2
  });

  return {
    pipelines: data || [],
    loading: isLoading,
    error: error as Error | null,
    refetch
  };
}

async function fetchPipelinesList(
  tenantId: number,
  userRole?: string,
  tipo?: PipelineType,
  listAll = false
): Promise<Pipeline[]> {
  let query = supabase
    .from('pipelines')
    .select('*')
    .or(`tenant_id.eq.${tenantId},tenant_ids.cs.[${tenantId}]`)
    .eq('ativa', true);

  if (tipo) {
    query = query.eq('tipo', tipo);
  }

  const { data, error } = await query.order('created_at', { ascending: true });

  if (error) {
    throw new Error(`Erro ao buscar pipelines: ${error.message}`);
  }

  let pipelines = data || [];

  if (!listAll) {
    // Admins veem TODAS as pipelines independentemente de perfil_usuario
    const isAdmin =
      userRole === ROLES.SUPER_ADMIN ||
      userRole === ROLES.SECRETARIA ||
      userRole === ROLES.ASSOCIADO;

    if (!isAdmin) {
      const perfilUsuario = mapRoleToPerfilUsuario(userRole);
      if (perfilUsuario) {
        pipelines = pipelines.filter(p => pipelineIncludesPerfil(p.perfil_usuario, perfilUsuario));
      }
    }
  }

  // Fixas sempre no topo, na ordem do processo (Qualificação → Comercial → Pós-venda);
  // as demais mantêm a ordem de criação (ordenação estável, já vem ascendente da query).
  pipelines = [...pipelines].sort((a, b) => {
    if (a.fixa !== b.fixa) return a.fixa ? -1 : 1;
    if (a.fixa && b.fixa) {
      return PIPELINE_TYPE_ORDER[a.tipo as PipelineType] - PIPELINE_TYPE_ORDER[b.tipo as PipelineType];
    }
    return 0;
  });

  return pipelines.map(p => ({
    ...p,
    perfil_usuario: p.perfil_usuario as PipelinePerfilUsuario | PipelinePerfilUsuario[],
    tenant_ids: p.tenant_ids as number[] | undefined,
  } as Pipeline));
}

function pipelineIncludesPerfil(
  perfilUsuario: unknown,
  perfil: PipelinePerfilUsuario
): boolean {
  if (Array.isArray(perfilUsuario)) {
    return perfilUsuario.includes(perfil);
  }
  return perfilUsuario === perfil;
}

function mapRoleToPerfilUsuario(userRole?: string): PipelinePerfilUsuario | null {
  const roleMap: Record<string, PipelinePerfilUsuario> = {
    [ROLES.RELACIONADOR]: 'relacionador',
    [ROLES.LIDER_VENDAS]: 'lider_vendas',
    [ROLES.SUPERVISOR]: 'supervisor',
    [ROLES.POS_VENDA]: 'especialista_pos_venda',
    [ROLES.SUPER_ADMIN]: 'admin',
    [ROLES.ASSOCIADO]: 'admin',
    [ROLES.SECRETARIA]: 'admin',
  };

  return roleMap[userRole || ''] || null;
}

/**
 * Roles autorizados pela policy de RLS (INSERT) da tabela `pipelines` no banco.
 * Mantido em sincronia com a policy `pipelines_insert_policy` no Supabase.
 */
const PIPELINE_CREATOR_ROLES: string[] = [
  ROLES.SUPER_ADMIN,
  ROLES.ASSOCIADO,
  ROLES.SECRETARIA,
  ROLES.SUPERVISOR,
  ROLES.REPRESENTANTE,
];

/**
 * Indica se o role pode criar novas pipelines.
 */
export function canCreatePipeline(role?: string): boolean {
  return !!role && PIPELINE_CREATOR_ROLES.includes(role);
}

/**
 * Tipos de pipeline visíveis por role do sistema
 */
/**
 * Tipos de pipeline que cada role pode acessar
 * Baseado na documentação de regras de negócio
 */
export function getPipelineTypesForRole(role?: string): PipelineType[] {
  const roleMap: Record<string, PipelineType[]> = {
    // Relacionador: apenas qualificação
    [ROLES.RELACIONADOR]: ['qualificacao'],

    // Líder de vendas: qualificação + venda
    [ROLES.LIDER_VENDAS]: ['qualificacao', 'venda'],

    // Especialista pós-venda: apenas pós-venda
    [ROLES.POS_VENDA]: ['pos_venda'],

    // Supervisor: todas as pipelines (qualificação, venda, pós-venda)
    [ROLES.SUPERVISOR]: ['qualificacao', 'venda', 'pos_venda'],

    // Administrativos: todas para admin/config
    [ROLES.SUPER_ADMIN]: ['qualificacao', 'venda', 'pos_venda'],
    [ROLES.SECRETARIA]: ['qualificacao', 'venda', 'pos_venda'],
    [ROLES.ASSOCIADO]: ['qualificacao', 'venda', 'pos_venda'],
  };

  return roleMap[role || ''] || [];
}
